import { ChevronDown } from "lucide-react";

import { Text } from "~/components/Text";
import type { EventFragment } from "~/__generated__/graphql/graphql";
import { useRootLocale } from "~/root";
import { formatLocalizedDate } from "~/utils/date";
import { centsToMoney } from "~/utils/money";

type TicketVariation = EventFragment["ticketVariations"][number];

type Props = {
  plan: EventFragment;
  ticketVariation?: TicketVariation;
  totalCostCents?: number | null;
  originalTotalCents?: number | null;
  discountAmountCents?: number;
  discountCode?: string;
  onEditTickets?: () => void;
  children?: React.ReactNode;
};

export function CheckoutSummaryCard({
  plan,
  ticketVariation,
  totalCostCents,
  originalTotalCents,
  discountAmountCents = 0,
  discountCode,
  onEditTickets,
  children,
}: Props) {
  const locale = useRootLocale();
  const hasDiscount = !!discountCode && discountAmountCents > 0;
  const ticketRowClassName = "flex min-h-11 min-w-0 items-center justify-between gap-3 border-t border-butter-green/10 px-3 py-2 text-butter-green";
  const ticketSummary = (
    <>
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span aria-hidden="true" className="shrink-0 text-base leading-none">
          {ticketVariation?.emoji || "🎟️"}
        </span>
        <Text
          as="span"
          variant="secondary"
          size="sm"
          weight="bold"
          color="green"
          className="block min-w-0 flex-1 break-words leading-tight"
        >
          {ticketVariation?.name ?? "Full access"}
        </Text>
      </span>
      {typeof totalCostCents === "number" && (
        <span className="flex shrink-0 flex-col items-end gap-0.5 text-right font-sans">
          {hasDiscount && typeof originalTotalCents === "number" && (
            <span className="text-sm line-through opacity-55">
              {centsToMoney(originalTotalCents, "$", locale)}
            </span>
          )}
          <span className="text-sm font-normal">
            {centsToMoney(totalCostCents, "$", locale)}
          </span>
        </span>
      )}
    </>
  );

  return (
    <section
      aria-label="Order summary"
      className="flex flex-col overflow-hidden rounded-xl border border-butter-green/10 bg-white/70"
    >
      <SummarySection
        className="flex min-w-0 items-center gap-3 px-3 py-3 text-butter-green transition-colors hover:bg-butter-green/5 focus-visible:bg-butter-green/5"
        onClick={onEditTickets}
        ariaLabel="Edit ticket selection"
      >
        <div
          className="h-11 w-11 shrink-0 rounded-lg bg-butter-cream bg-cover bg-center"
          style={{
            backgroundImage: `url(${plan.bannerImageUrl || "/images/default-plan-banner.png"})`,
          }}
        />
        <div className="min-w-0 flex-1">
          <Text
            as={onEditTickets ? "span" : "h3"}
            variant="secondary"
            size="md"
            weight="bold"
            className="block line-clamp-1 leading-tight"
          >
            {plan.title}
          </Text>
          <Text
            as={onEditTickets ? "span" : "p"}
            variant="secondary"
            size="sm"
            className="block line-clamp-2 leading-snug text-butter-green"
          >
            {formatLocalizedDate(plan.startDate, "eventCardDateTime", locale)}
          </Text>
        </div>
      </SummarySection>

      {children ? (
        <details className="group/price-breakdown">
          <summary className={`${ticketRowClassName} cursor-pointer list-none font-sans text-sm focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-current [&::-webkit-details-marker]:hidden`}>
            {ticketSummary}
            <span className="sr-only">Price details</span>
            <ChevronDown aria-hidden="true" className="size-3.5 shrink-0 group-open/price-breakdown:rotate-180" />
          </summary>
          <div className="px-3 pt-1 pb-3">
            {children}
          </div>
        </details>
      ) : (
        <SummarySection
          className={ticketRowClassName}
          onClick={onEditTickets}
          ariaLabel="Edit ticket selection"
        >
          {ticketSummary}
        </SummarySection>
      )}
    </section>
  );
}

type SummarySectionProps = {
  className: string;
  onClick?: () => void;
  ariaLabel: string;
  children: React.ReactNode;
};

function SummarySection({
  className,
  onClick,
  ariaLabel,
  children,
}: SummarySectionProps) {
  if (!onClick) return <div className={className}>{children}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`${className} cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-current`}
    >
      {children}
    </button>
  );
}
